import { useEffect, useRef, useState, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import { runProjectAnalysis, type AnalysisResult } from "@/lib/analysis.functions";
import { AnalysisReport } from "./AnalysisReport";

type Mode = "text" | "voice";

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
}

const ACCEPTED = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_SIZE_MB = 25;

function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-[var(--rule)] shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6 md:p-8 ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeading({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-[color-mix(in_oklab,var(--leaf)_12%,white)] text-[var(--leaf-deep)] shrink-0">
        {icon}
      </span>
      <h2 className="font-display font-bold text-navy text-lg md:text-xl">{children}</h2>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold text-navy mb-1.5">
        {label}
        {required && <span className="text-orange ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-navy/55">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full h-10 rounded-lg border border-[var(--rule)] bg-white px-3 text-sm text-navy placeholder:text-navy/35 outline-none focus:border-[var(--leaf)] focus:ring-2 focus:ring-[var(--leaf)]/20 transition";
const selectCls = inputCls + " appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%2364748b%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:18px] bg-[right_10px_center] bg-no-repeat pr-9";
const textareaCls =
  "w-full min-h-[120px] rounded-lg border border-[var(--rule)] bg-white px-3 py-2.5 text-sm text-navy placeholder:text-navy/35 outline-none focus:border-[var(--leaf)] focus:ring-2 focus:ring-[var(--leaf)]/20 transition resize-y";

type Details = {
  // Persönliche Daten
  name: string;
  email: string;
  phone: string;
  vorhabenKurz: string;
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  // Haus
  houseType: string;
  yearBuilt: string;
  units: string;
  livingArea: string;
  construction: string;
  windowMaterial: string;
  windowGlazing: string;
  entranceDoorMaterial: string;
  entranceDoorCondition: string;
  interiorDoorsMaterial: string;
  interiorDoorsCondition: string;
  roofType: string;
  roofFrame: string;
  renovationsDone: string;
  // Strom
  electricityConsumption: string;
  electricityProvider: string;
  electricityPrice: string;
  // Heizung
  heatingType: string;
  heatingYear: string;
  heatDistribution: string;
  heatingProvider: string;
  heatingConsumption: string;
  hotWaterSource: string;
  hotWaterType: string;
  // Förderung
  foerderungBeantragt: string; // "ja" | "nein"
  foerderungenSelected: string[];
  // Energetische Systeme – PV
  hasPv: string; // "ja" | "nein" | "geplant"
  pvKwp: string;
  pvModuleType: string; // Glas-Folie, Glas-Glas, ...
  pvYear: string;
  pvEinspeisung: string;
  // Batteriespeicher
  hasBattery: string;
  batteryBrand: string;
  batteryKwh: string;
  // Energieausweis
  energyCertificate: string;
  energyClass: string; // A+ ... H
  // Steuer
  taxClass: string;
  taxRate: string;
  taxableIncome: string;
  // Ziel
  projectGoals: string[];
};

const initial: Details = {
  name: "", email: "", phone: "", vorhabenKurz: "",
  street: "", houseNumber: "", zip: "", city: "",
  houseType: "", yearBuilt: "", units: "", livingArea: "",
  construction: "", windowMaterial: "", windowGlazing: "",
  entranceDoorMaterial: "", entranceDoorCondition: "",
  interiorDoorsMaterial: "", interiorDoorsCondition: "",
  roofType: "", roofFrame: "", renovationsDone: "",
  electricityConsumption: "", electricityProvider: "", electricityPrice: "",
  heatingType: "", heatingYear: "", heatDistribution: "",
  heatingProvider: "", heatingConsumption: "",
  hotWaterSource: "", hotWaterType: "",
  foerderungBeantragt: "", foerderungenSelected: [],
  hasPv: "", pvKwp: "", pvModuleType: "", pvYear: "", pvEinspeisung: "",
  hasBattery: "", batteryBrand: "", batteryKwh: "",
  energyCertificate: "", energyClass: "",
  taxClass: "", taxRate: "", taxableIncome: "",
  projectGoals: [],
};

const REQUIRED: (keyof Details)[] = [
  "name", "email", "street", "houseNumber", "zip", "city",
  "houseType", "yearBuilt", "livingArea", "heatingType",
  "electricityConsumption",
];

const FOERDER_OPTIONS = [
  "BAFA – Heizungsförderung",
  "BAFA – Energieberatung",
  "KfW 261 – Wohngebäude Kredit",
  "KfW 270 – Erneuerbare Energien",
  "KfW 458 – Heizungsförderung Zuschuss",
  "iSFP – Sanierungsfahrplan",
  "Landesförderung",
  "Kommunale Förderung",
  "Steuerliche Förderung §35c EStG",
  "EEG-Einspeisevergütung",
  "Sonstige Förderung",
];

const PROJECT_GOAL_OPTIONS = [
  "Photovoltaik",
  "Batteriespeicher",
  "Wallbox / E-Mobilität",
  "Wärmepumpe",
  "Hybridheizung",
  "Solarthermie",
  "Lüftungsanlage mit Wärmerückgewinnung",
  "Dachsanierung",
  "Dachdämmung",
  "Fassadendämmung",
  "Kellerdeckendämmung",
  "Fensteraustausch",
  "Eingangstür / Haustür",
  "Heizungserneuerung",
  "Komplettsanierung",
  "Energetische Sanierung",
  "Neubau",
  "Wohnraumerweiterung / Aufstockung",
  "Smart-Home / Energiemanagement",
  "Fördermittelprüfung",
  "Angebotsvergleich",
  "Steuerliche Prüfung",
  "Mieterstrom / Mieterwärme",
  "Kombination / Gesamtprojekt",
  "Sonstiges",
];

const ENERGY_CLASS_OPTIONS = ["A+","A","B","C","D","E","F","G","H"];

export function ProjektBeschreibenForm() {
  const [d, setD] = useState<Details>(initial);
  const [mode, setMode] = useState<Mode>("text");
  const [freeText, setFreeText] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Details, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const runAnalysis = useServerFn(runProjectAnalysis);

  const set = <K extends keyof Details>(k: K, v: Details[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));

  const toggleInArray = (key: "foerderungenSelected" | "projectGoals", value: string) =>
    setD((prev) => {
      const arr = prev[key];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as unknown as {
      SpeechRecognition?: new () => SpeechRecognitionLike;
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    };
    const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!Ctor) { setVoiceSupported(false); return; }
    const rec = new Ctor();
    rec.lang = "de-DE";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let finalTxt = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalTxt += r[0].transcript;
      }
      if (finalTxt) setFreeText((prev) => (prev ? prev + " " : "") + finalTxt.trim());
    };
    rec.onerror = () => {
      setListening(false);
      setVoiceError("Mikrofonzugriff verweigert oder Spracherkennung nicht möglich.");
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch { /* noop */ } };
  }, []);

  const toggleListening = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    setVoiceError(null);
    if (listening) { rec.stop(); setListening(false); }
    else { try { rec.start(); setListening(true); } catch { /* already started */ } }
  };

  const validateAndAddFiles = (list: FileList | null) => {
    if (!list) return;
    setUploadError(null);
    const ok: File[] = [];
    for (const f of Array.from(list)) {
      const ext = "." + (f.name.split(".").pop() ?? "").toLowerCase();
      if (!ACCEPTED.includes(ext)) {
        setUploadError(`Format „${ext}" nicht erlaubt. Erlaubt: PDF, JPG, PNG.`);
        continue;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        setUploadError(`„${f.name}" überschreitet ${MAX_SIZE_MB} MB.`);
        continue;
      }
      ok.push(f);
    }
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
  };

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const validate = (): boolean => {
    const e: Partial<Record<keyof Details, string>> = {};
    for (const k of REQUIRED) {
      const v = d[k];
      if (typeof v === "string" && !v.trim()) e[k] = "Pflichtfeld";
    }
    if (d.projectGoals.length === 0) e.projectGoals = "Bitte mind. eine Option wählen";
    if (d.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) e.email = "Ungültige E-Mail";
    if (d.yearBuilt && !/^\d{4}$/.test(d.yearBuilt)) e.yearBuilt = "4-stellige Jahreszahl";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitError(null);
    if (!validate()) {
      const first = document.querySelector("[data-error='1']") as HTMLElement | null;
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const extraNotes = [
        d.foerderungBeantragt === "ja" && d.foerderungenSelected.length
          ? `Bereits beantragte Förderungen: ${d.foerderungenSelected.join(", ")}`
          : null,
        d.hasPv === "ja" && (d.pvKwp || d.pvModuleType || d.pvYear)
          ? `PV-Anlage: ${[d.pvKwp && `${d.pvKwp} kWp`, d.pvModuleType, d.pvYear && `BJ ${d.pvYear}`, d.pvEinspeisung && `Einspeisung: ${d.pvEinspeisung}`].filter(Boolean).join(", ")}`
          : null,
        d.hasBattery === "ja" && (d.batteryBrand || d.batteryKwh)
          ? `Batteriespeicher: ${[d.batteryBrand, d.batteryKwh && `${d.batteryKwh} kWh`].filter(Boolean).join(", ")}`
          : null,
        d.electricityPrice ? `Strompreis: ${d.electricityPrice} ct/kWh` : null,
        d.phone ? `Telefon: ${d.phone}` : null,
      ].filter(Boolean).join("\n");

      const res = await runAnalysis({
        data: {
          street: d.street, houseNumber: d.houseNumber, zip: d.zip, city: d.city,
          houseType: d.houseType, units: d.units, yearBuilt: d.yearBuilt,
          construction: d.construction, windowMaterial: d.windowMaterial,
          windowGlazing: d.windowGlazing, entranceDoorMaterial: d.entranceDoorMaterial,
          entranceDoorCondition: d.entranceDoorCondition,
          interiorDoorsMaterial: d.interiorDoorsMaterial,
          interiorDoorsCondition: d.interiorDoorsCondition,
          roofType: d.roofType, roofInsulated: d.renovationsDone,
          renovationsDone: d.renovationsDone, renovationsDetails: "",
          heatingType: d.heatingType,
          heatingConsumption: d.heatingConsumption,
          electricityConsumption: d.electricityConsumption,
          electricityProvider: d.electricityProvider,
          energyCertificate: d.energyCertificate, energyClass: d.energyClass,
          taxClass: d.taxClass, taxRate: d.taxRate, taxableIncome: d.taxableIncome,
          projectGoal: d.projectGoals.join(", "),
          projectDescription: d.vorhabenKurz,
          freeText: [freeText, extraNotes].filter(Boolean).join("\n\n"),
        },
      });
      if ("analysis" in res && res.analysis) {
        setAnalysis(res.analysis);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setSubmitError("Analyse konnte nicht erstellt werden. Bitte erneut versuchen.");
      }
    } catch (err) {
      console.error("[ProjektBeschreibenForm] submit error:", err);
      setSubmitError("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (analysis) {
    return (
      <section className="border-b border-[var(--rule)] bg-[#f4f6fa]">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 py-10 md:py-14">
          <AnalysisReport data={analysis} onClose={() => setAnalysis(null)} />
        </div>
      </section>
    );
  }

  const opt = (xs: string[]) => xs.map((x) => <option key={x} value={x}>{x}</option>);
  const err = (k: keyof Details) =>
    errors[k] ? <span data-error="1" className="mt-1 block text-[11px] text-red-600">{errors[k]}</span> : null;

  const CheckboxGrid = ({
    options, selected, onToggle, cols = 2,
  }: { options: string[]; selected: string[]; onToggle: (v: string) => void; cols?: 2 | 3 }) => (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : ""} gap-2`}>
      {options.map((o) => {
        const active = selected.includes(o);
        return (
          <label
            key={o}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition text-sm ${
              active
                ? "border-[var(--leaf)] bg-[color-mix(in_oklab,var(--leaf)_10%,white)] text-navy"
                : "border-[var(--rule)] bg-white hover:border-[var(--leaf)]/60 text-navy/80"
            }`}
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => onToggle(o)}
              className="w-4 h-4 rounded border-[var(--rule)] text-[var(--leaf-deep)] focus:ring-[var(--leaf)]/30 accent-[var(--leaf-deep)]"
            />
            <span>{o}</span>
          </label>
        );
      })}
    </div>
  );

  return (
    <section className="border-b border-[var(--rule)] bg-[#f4f6fa]">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10 py-10 md:py-14">
        <h1 className="font-display font-bold text-navy text-3xl md:text-5xl leading-[1.1]">
          Ihr Vorhaben beschreiben
        </h1>
        <p className="mt-4 text-navy/75 max-w-3xl text-sm md:text-base leading-relaxed">
          Bitte füllen Sie die Bereiche der Reihe nach aus und laden Sie anschließend
          die entsprechenden Unterlagen über den unten aufgeführten Bereich hoch.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-6">
          {/* === 1) PERSÖNLICHE DATEN === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4 0-9 2-9 6v2h18v-2c0-4-5-6-9-6z"/></svg>}
            >
              1. Persönliche Daten
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Name" required>
                <input className={inputCls} placeholder="Ihr vollständiger Name" value={d.name} onChange={(e) => set("name", e.target.value)} />
                {err("name")}
              </Field>
              <Field label="E-Mail" required>
                <input type="email" className={inputCls} placeholder="Ihre E-Mail-Adresse" value={d.email} onChange={(e) => set("email", e.target.value)} />
                {err("email")}
              </Field>
              <Field label="Telefon">
                <input className={inputCls} placeholder="z. B. 0151 …" value={d.phone} onChange={(e) => set("phone", e.target.value)} />
              </Field>
              <Field label="Straße" required>
                <input className={inputCls} placeholder="Straßenname" value={d.street} onChange={(e) => set("street", e.target.value)} />
                {err("street")}
              </Field>
              <Field label="Hausnummer" required>
                <input className={inputCls} placeholder="z. B. 12" value={d.houseNumber} onChange={(e) => set("houseNumber", e.target.value)} />
                {err("houseNumber")}
              </Field>
              <Field label="PLZ" required>
                <input className={inputCls} placeholder="z. B. 12345" value={d.zip} onChange={(e) => set("zip", e.target.value)} />
                {err("zip")}
              </Field>
              <Field label="Ort" required>
                <input className={inputCls} placeholder="z. B. Musterstadt" value={d.city} onChange={(e) => set("city", e.target.value)} />
                {err("city")}
              </Field>
              <Field label="Vorhaben kurz beschreiben">
                <input className={inputCls} placeholder="z. B. PV + Speicher + Wärmepumpe" value={d.vorhabenKurz} onChange={(e) => set("vorhabenKurz", e.target.value)} />
              </Field>
            </div>
          </Card>

          {/* === 2) ANGABEN ZUM HAUS === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 3l9 8h-3v9h-5v-6h-2v6H6v-9H3l9-8z"/></svg>}
            >
              2. Angaben zum Haus
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Art des Hauses" required>
                <select className={selectCls} value={d.houseType} onChange={(e) => set("houseType", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Einfamilienhaus","Zweifamilienhaus","Mehrfamilienhaus","Reihenhaus","Doppelhaushälfte","Eigentumswohnung","Wohn- und Geschäftshaus","Gewerbeobjekt","Sonstiges"])}
                </select>
                {err("houseType")}
              </Field>
              <Field label="Baujahr" required>
                <input className={inputCls} placeholder="z. B. 1995" value={d.yearBuilt} onChange={(e) => set("yearBuilt", e.target.value)} />
                {err("yearBuilt")}
              </Field>
              <Field label="Anzahl Mieter / Wohnungen">
                <input className={inputCls} placeholder="z. B. 2" value={d.units} onChange={(e) => set("units", e.target.value)} />
              </Field>
              <Field label="Wohnfläche (m²)" required>
                <input className={inputCls} placeholder="z. B. 180" value={d.livingArea} onChange={(e) => set("livingArea", e.target.value)} />
                {err("livingArea")}
              </Field>
              <Field label="Bauweise / Material">
                <select className={selectCls} value={d.construction} onChange={(e) => set("construction", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Massivbau","Ziegel / Mauerwerk","Beton","Holzbau","Fachwerk","Stahlbau","Mischbauweise","Sonstiges"])}
                </select>
              </Field>
              <Field label="Fenster – Material">
                <select className={selectCls} value={d.windowMaterial} onChange={(e) => set("windowMaterial", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Kunststoff","Holz","Aluminium","Holz-Aluminium","Sonstiges"])}
                </select>
              </Field>
              <Field label="Fenster – Verglasung">
                <select className={selectCls} value={d.windowGlazing} onChange={(e) => set("windowGlazing", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Einfachverglasung","Doppelverglasung","Dreifachverglasung","Unbekannt"])}
                </select>
              </Field>
              <Field label="Eingangstür – Material">
                <select className={selectCls} value={d.entranceDoorMaterial} onChange={(e) => set("entranceDoorMaterial", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Holz","Kunststoff","Aluminium","Stahl","Glas","Sonstiges"])}
                </select>
              </Field>
              <Field label="Eingangstür – Zustand">
                <select className={selectCls} value={d.entranceDoorCondition} onChange={(e) => set("entranceDoorCondition", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Sehr gut","Gut","Mittel","Sanierungsbedürftig","Unbekannt"])}
                </select>
              </Field>
              <Field label="Innentüren – Material">
                <select className={selectCls} value={d.interiorDoorsMaterial} onChange={(e) => set("interiorDoorsMaterial", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Holz","CPL / Dekor","Glas","Metall","Sonstiges"])}
                </select>
              </Field>
              <Field label="Innentüren – Zustand">
                <select className={selectCls} value={d.interiorDoorsCondition} onChange={(e) => set("interiorDoorsCondition", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Sehr gut","Gut","Mittel","Sanierungsbedürftig","Unbekannt"])}
                </select>
              </Field>
              <Field label="Art des Daches">
                <select className={selectCls} value={d.roofType} onChange={(e) => set("roofType", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Satteldach","Pultdach","Flachdach","Walmdach","Zeltdach","Mansarddach","Sonstiges"])}
                </select>
              </Field>
              <Field label="Dachstuhl">
                <select className={selectCls} value={d.roofFrame} onChange={(e) => set("roofFrame", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Holz","Stahl","Beton","Unbekannt","Nicht vorhanden"])}
                </select>
              </Field>
              <Field label="Sanierungen / Dämmung bereits erfolgt?">
                <select className={selectCls} value={d.renovationsDone} onChange={(e) => set("renovationsDone", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Ja, vollständig","Ja, teilweise","Nein","Unbekannt"])}
                </select>
              </Field>
            </div>
          </Card>

          {/* === 3) STROM === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>}
            >
              3. Strom
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Strom – Jahresverbrauch (kWh)" required>
                <input className={inputCls} placeholder="z. B. 4.500" value={d.electricityConsumption} onChange={(e) => set("electricityConsumption", e.target.value)} />
                {err("electricityConsumption")}
              </Field>
              <Field label="Strom – Anbieter">
                <input className={inputCls} placeholder="z. B. Stadtwerke, E.ON …" value={d.electricityProvider} onChange={(e) => set("electricityProvider", e.target.value)} />
              </Field>
              <Field label="Strompreis (ct/kWh)">
                <input className={inputCls} placeholder="z. B. 38" value={d.electricityPrice} onChange={(e) => set("electricityPrice", e.target.value)} />
              </Field>
            </div>
          </Card>

          {/* === 4) HEIZUNG === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 2s5 5 5 10a5 5 0 11-10 0c0-5 5-10 5-10z"/></svg>}
            >
              4. Heizung
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Heizung – Art" required>
                <select className={selectCls} value={d.heatingType} onChange={(e) => set("heatingType", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Gasheizung","Ölheizung","Wärmepumpe","Fernwärme","Pelletheizung","Holzheizung","Nachtspeicherheizung","Hybridheizung","Sonstiges"])}
                </select>
                {err("heatingType")}
              </Field>
              <Field label="Baujahr Heizung">
                <input className={inputCls} placeholder="z. B. 2008" value={d.heatingYear} onChange={(e) => set("heatingYear", e.target.value)} />
              </Field>
              <Field label="Wärmeübergabe">
                <select className={selectCls} value={d.heatDistribution} onChange={(e) => set("heatDistribution", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Heizkörper","Fußbodenheizung","Wandheizung","Deckenheizung","Kombination","Unbekannt"])}
                </select>
              </Field>
              <Field label="Heizung – Anbieter">
                <input className={inputCls} placeholder="z. B. Stadtwerke, E.ON, Shell …" value={d.heatingProvider} onChange={(e) => set("heatingProvider", e.target.value)} />
              </Field>
              <Field label="Heizung – Jahresverbrauch">
                <input className={inputCls} placeholder="z. B. 18.000 kWh / 1.800 l Öl" value={d.heatingConsumption} onChange={(e) => set("heatingConsumption", e.target.value)} />
              </Field>
              <Field label="Warmwasser – Erzeugung">
                <select className={selectCls} value={d.hotWaterSource} onChange={(e) => set("hotWaterSource", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Über Heizung","Elektrisch","Durchlauferhitzer","Boiler / Speicher","Solar unterstützt","Sonstiges"])}
                </select>
              </Field>
              <Field label="Warmwasser – Art">
                <select className={selectCls} value={d.hotWaterType} onChange={(e) => set("hotWaterType", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Zentral","Dezentral","Kombination","Unbekannt"])}
                </select>
              </Field>
            </div>
          </Card>

          {/* === 5) FÖRDERMITTEL === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 1l3.09 6.26L22 8.27l-5 4.87 1.18 6.88L12 16.77l-6.18 3.25L7 13.14 2 8.27l6.91-1.01L12 1z"/></svg>}
            >
              5. Fördermittel
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <Field label="Wurden bereits Fördermittel beantragt?">
                <select className={selectCls} value={d.foerderungBeantragt} onChange={(e) => set("foerderungBeantragt", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Nein","Ja","In Prüfung"])}
                </select>
              </Field>
            </div>
            {d.foerderungBeantragt === "Ja" && (
              <div className="mt-5">
                <p className="text-[12px] font-semibold text-navy mb-2">Welche Fördermittel wurden beantragt? (Mehrfachauswahl)</p>
                <CheckboxGrid
                  options={FOERDER_OPTIONS}
                  selected={d.foerderungenSelected}
                  onToggle={(v) => toggleInArray("foerderungenSelected", v)}
                  cols={3}
                />
              </div>
            )}
          </Card>

          {/* === 6) ENERGETISCHE SYSTEME === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M4 4h16v12H4zM2 18h20v2H2zm10-12L9 12h2v3l3-6h-2V6z"/></svg>}
            >
              6. Energetische Systeme
            </SectionHeading>

            {/* PV */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="PV-Anlage vorhanden?">
                <select className={selectCls} value={d.hasPv} onChange={(e) => set("hasPv", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Ja","Nein","Geplant"])}
                </select>
              </Field>
              {d.hasPv === "Ja" && (
                <>
                  <Field label="Leistung (kWp)">
                    <input className={inputCls} placeholder="z. B. 9,8" value={d.pvKwp} onChange={(e) => set("pvKwp", e.target.value)} />
                  </Field>
                  <Field label="Modul-Typ">
                    <select className={selectCls} value={d.pvModuleType} onChange={(e) => set("pvModuleType", e.target.value)}>
                      <option value="">Bitte wählen</option>
                      {opt(["Glas-Folie","Glas-Glas","Bifazial","Full-Black","Indach","Solardachziegel","Unbekannt","Sonstiges"])}
                    </select>
                  </Field>
                  <Field label="Baujahr PV">
                    <input className={inputCls} placeholder="z. B. 2021" value={d.pvYear} onChange={(e) => set("pvYear", e.target.value)} />
                  </Field>
                  <Field label="Einspeisung">
                    <select className={selectCls} value={d.pvEinspeisung} onChange={(e) => set("pvEinspeisung", e.target.value)}>
                      <option value="">Bitte wählen</option>
                      {opt(["Volleinspeisung","Überschusseinspeisung","Eigenverbrauch ohne Einspeisung","Unbekannt"])}
                    </select>
                  </Field>
                </>
              )}
            </div>

            {/* Batterie */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Batteriespeicher vorhanden?">
                <select className={selectCls} value={d.hasBattery} onChange={(e) => set("hasBattery", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Ja","Nein","Geplant"])}
                </select>
              </Field>
              {d.hasBattery === "Ja" && (
                <>
                  <Field label="Marke / Hersteller">
                    <input className={inputCls} placeholder="z. B. BYD, Sonnen, Huawei …" value={d.batteryBrand} onChange={(e) => set("batteryBrand", e.target.value)} />
                  </Field>
                  <Field label="Kapazität (kWh)">
                    <input className={inputCls} placeholder="z. B. 10" value={d.batteryKwh} onChange={(e) => set("batteryKwh", e.target.value)} />
                  </Field>
                </>
              )}
            </div>

            {/* Energieausweis */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Energieausweis vorhanden?">
                <select className={selectCls} value={d.energyCertificate} onChange={(e) => set("energyCertificate", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["Ja","Nein","In Erstellung"])}
                </select>
              </Field>
              {d.energyCertificate === "Ja" && (
                <Field label="Energieeffizienzklasse">
                  <select className={selectCls} value={d.energyClass} onChange={(e) => set("energyClass", e.target.value)}>
                    <option value="">Bitte wählen</option>
                    {opt(ENERGY_CLASS_OPTIONS)}
                  </select>
                </Field>
              )}
            </div>
          </Card>

          {/* === 7) STEUERLICHE ANGABEN === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M4 2h12l4 4v16H4zM14 2v6h6"/></svg>}
            >
              7. Steuerliche Angaben
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
              <Field label="Steuerklasse">
                <select className={selectCls} value={d.taxClass} onChange={(e) => set("taxClass", e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {opt(["I","II","III","IV","IV mit Faktor","V","VI"])}
                </select>
              </Field>
              <Field label="Steuersatz (%)">
                <input className={inputCls} placeholder="z. B. 42" value={d.taxRate} onChange={(e) => set("taxRate", e.target.value)} />
              </Field>
              <Field
                label="Zu versteuerndes Einkommen / Jahr (€)"
                hint={
                  <span className="inline-flex items-start gap-1">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 mt-0.5 text-navy/50 shrink-0" fill="currentColor"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
                    <span>Wichtig für die Bewertung von AfA und steuerlichen Effekten.</span>
                  </span>
                }
              >
                <input className={inputCls} placeholder="z. B. 85.000" value={d.taxableIncome} onChange={(e) => set("taxableIncome", e.target.value)} />
              </Field>
            </div>
          </Card>

          {/* === 8) WAS MÖCHTEN SIE MACHEN === */}
          <Card>
            <SectionHeading
              icon={<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19l12-12-1.4-1.4z"/></svg>}
            >
              8. Was möchten Sie machen? <span className="text-xs font-normal text-navy/55 ml-1">(Mehrfachauswahl)</span>
            </SectionHeading>
            <p className="text-sm text-navy/70 mb-3">
              Wählen Sie alle Bereiche aus, die für Ihr Vorhaben relevant sind – Sie können mehrere Punkte anklicken.
            </p>
            <CheckboxGrid
              options={PROJECT_GOAL_OPTIONS}
              selected={d.projectGoals}
              onToggle={(v) => toggleInArray("projectGoals", v)}
              cols={3}
            />
            {errors.projectGoals && (
              <span data-error="1" className="mt-2 block text-[11px] text-red-600">{errors.projectGoals}</span>
            )}
          </Card>


          {/* === KARTE 2: Zusatz === */}
          <Card>
            <SectionHeading
              icon={
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
                  <path d="M4 4h16a2 2 0 012 2v10a2 2 0 01-2 2H8l-4 4V6a2 2 0 012-2z"/>
                </svg>
              }
            >
              Zusätzliche Informationen & Beschreibungen zum Projekt
            </SectionHeading>

            <div className="inline-flex rounded-lg border border-[var(--rule)] bg-[#f4f6fa] p-1 mb-4">
              <button
                type="button"
                onClick={() => setMode("text")}
                className={`inline-flex items-center gap-2 px-4 h-9 rounded-md text-sm font-semibold transition ${mode === "text" ? "bg-navy text-white shadow" : "text-navy/70 hover:text-navy"}`}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.42l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.82z"/></svg>
                Schreiben
              </button>
              <button
                type="button"
                onClick={() => setMode("voice")}
                disabled={!voiceSupported}
                className={`inline-flex items-center gap-2 px-4 h-9 rounded-md text-sm font-semibold transition ${mode === "voice" ? "bg-navy text-white shadow" : "text-navy/70 hover:text-navy"} ${!voiceSupported ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 14a3 3 0 003-3V5a3 3 0 10-6 0v6a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.92V21h2v-3.08A7 7 0 0019 11h-2z"/></svg>
                Sprechen
              </button>
            </div>

            <p className="text-sm text-navy/70 mb-3 leading-relaxed">
              Schildern Sie Ihr Vorhaben zusätzlich per Text oder Sprache. Je mehr Informationen wir erhalten,
              desto präziser fällt Ihre Analyse aus.
            </p>

            {mode === "voice" && (
              <div className="mb-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleListening}
                  className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition ${listening ? "bg-red-600 text-white" : "bg-[var(--leaf-deep)] text-white hover:bg-[var(--leaf)]"}`}
                >
                  <span className={`w-2 h-2 rounded-full ${listening ? "bg-white animate-pulse" : "bg-white/80"}`} />
                  {listening ? "Aufnahme stoppen" : "Aufnahme starten"}
                </button>
                {voiceError && <span className="text-xs text-red-600">{voiceError}</span>}
                {!voiceSupported && <span className="text-xs text-navy/60">Spracherkennung in diesem Browser nicht verfügbar.</span>}
              </div>
            )}

            <textarea
              className={textareaCls}
              placeholder="Ergänzende Hinweise zu Ihrem Vorhaben, Zielen, offenen Fragen …"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
            />
          </Card>

          {/* === KARTE 3: Upload === */}
          <Card>
            <div className="flex items-start gap-4 mb-5">
              <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[var(--leaf-deep)] text-white shrink-0">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor"><path d="M19.35 10.04A7.49 7.49 0 0012 4a7.5 7.5 0 00-6.98 4.78A5.5 5.5 0 006 20h13a4.5 4.5 0 00.35-9.96zM13 13v4h-2v-4H8l4-4 4 4h-3z"/></svg>
              </span>
              <div className="min-w-0">
                <h2 className="font-display font-bold text-navy text-lg md:text-xl">Laden Sie Ihre Unterlagen & Angebote hoch</h2>
                <p className="mt-1 text-sm text-navy/70">
                  Digitale Bauakte, Energieausweis, Grundrisse, Rechnungen, Angebote · PDF, JPG, PNG
                </p>
              </div>
            </div>

            <p className="text-sm text-navy/75 mb-4 leading-relaxed">
              Laden Sie alle relevanten Unterlagen hier hoch. Wir prüfen alle Möglichkeiten
              und erstellen Ihre persönliche Analyse & Handlungsempfehlung.
            </p>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); validateAndAddFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-xl border-2 border-dashed transition cursor-pointer text-center p-10 ${dragging ? "border-[var(--leaf)] bg-[color-mix(in_oklab,var(--leaf)_8%,white)]" : "border-[var(--leaf)]/50 bg-[color-mix(in_oklab,var(--leaf)_4%,white)] hover:bg-[color-mix(in_oklab,var(--leaf)_8%,white)]"}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => validateAndAddFiles(e.target.files)}
              />
              <span className="mx-auto mb-3 inline-flex w-14 h-14 items-center justify-center rounded-full bg-white shadow-sm">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-[var(--leaf-deep)]" fill="currentColor"><path d="M12 3l5 5h-3v6h-4V8H7l5-5zM5 18v2h14v-2H5z"/></svg>
              </span>
              <div className="font-display font-semibold text-navy text-sm">
                Dateien hierher ziehen oder klicken, um Dateien auszuwählen
              </div>
              <div className="mt-2 text-xs text-navy/60">PDF, JPG, PNG · max. {MAX_SIZE_MB} MB pro Datei</div>
            </div>

            {uploadError && <p className="mt-3 text-xs text-red-600">{uploadError}</p>}

            {files.length > 0 && (
              <ul className="mt-4 divide-y divide-[var(--rule)] border border-[var(--rule)] rounded-lg overflow-hidden">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 text-[var(--leaf-deep)] shrink-0" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/></svg>
                      <div className="min-w-0">
                        <div className="text-sm text-navy truncate">{f.name}</div>
                        <div className="text-[11px] text-navy/55">{(f.size / 1024).toFixed(0)} KB</div>
                      </div>
                    </div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-xs text-red-600 hover:underline shrink-0">
                      Entfernen
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* === CTA-Karte === */}
          <div className="bg-white rounded-2xl border border-[var(--rule)] shadow-sm p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex items-start gap-3 flex-1">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--leaf)_12%,white)] text-[var(--leaf-deep)] shrink-0">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 1l9 4v6c0 5.5-3.8 10.7-9 12-5.2-1.3-9-6.5-9-12V5l9-4z"/></svg>
              </span>
              <p className="text-sm text-navy/80 leading-relaxed">
                Klicken Sie abschließend auf „Kostenlose Bewertung anfordern" und erhalten Sie
                Ihre kostenlose Analyse & Handlungsempfehlung.
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 bg-orange hover:opacity-90 text-white font-semibold px-6 h-12 rounded-lg text-sm transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            >
              {submitting ? "Analyse läuft …" : "Kostenlose Bewertung anfordern"}
              {!submitting && <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M5 12h12l-4-4 1.4-1.4L20.8 12l-6.4 5.4L13 16l4-4H5z"/></svg>}
            </button>
          </div>

          {submitError && (
            <p className="text-sm text-red-600 text-center">{submitError}</p>
          )}
          {Object.keys(errors).length > 0 && (
            <p className="text-sm text-red-600 text-center">Bitte füllen Sie alle Pflichtfelder korrekt aus.</p>
          )}
        </form>
      </div>
    </section>
  );
}
